import React from "react"
import { useState } from "react"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Loader2, Copy, Check } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { LANGUAGES } from "@/lib/constants"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"

type EmailCategory = {
  id: string
  name: string
  description: string
  fields: Array<{
    id: string
    name: string
    description: string
    type: "text" | "textarea"
  }>
}

interface EmailSettings {
  tone: string
  length: string
  language: string
  includeImages: boolean
}

const emailCategories: EmailCategory[] = [
  {
    id: "donor_thank",
    name: "Donor Thank You",
    description: "Express gratitude and share impact",
    fields: [
      {
        id: "donor_name",
        name: "Donor Name",
        description: "Name of the donor to thank",
        type: "text"
      },
      {
        id: "donation_amount",
        name: "Donation Amount",
        description: "Amount donated (optional)",
        type: "text"
      },
      {
        id: "impact_description",
        name: "Impact Description",
        description: "How the donation will be used",
        type: "textarea"
      }
    ]
  },
  {
    id: "fundraising",
    name: "Fundraising Appeal",
    description: "Request donations for your cause",
    fields: [
      {
        id: "campaign_name",
        name: "Campaign Name",
        description: "Name of your fundraising campaign",
        type: "text"
      },
      {
        id: "goal_amount",
        name: "Goal Amount",
        description: "Fundraising goal (optional)",
        type: "text"
      },
      {
        id: "campaign_description",
        name: "Campaign Description",
        description: "Details about the campaign and its impact",
        type: "textarea"
      }
    ]
  },
  {
    id: "newsletter",
    name: "Newsletter",
    description: "Share updates and news",
    fields: [
      {
        id: "main_story",
        name: "Main Story",
        description: "Primary news or update to share",
        type: "textarea"
      },
      {
        id: "recent_achievements",
        name: "Recent Achievements",
        description: "Key accomplishments to highlight",
        type: "textarea"
      },
      {
        id: "upcoming_events",
        name: "Upcoming Events",
        description: "List of upcoming events or opportunities",
        type: "textarea"
      }
    ]
  },
  {
    id: "event",
    name: "Event Invitation",
    description: "Invite supporters to your events",
    fields: [
      {
        id: "event_name",
        name: "Event Name",
        description: "Name of your event",
        type: "text"
      },
      {
        id: "event_date",
        name: "Event Date & Time",
        description: "When the event will take place",
        type: "text"
      },
      {
        id: "event_location",
        name: "Event Location",
        description: "Where the event will be held",
        type: "text"
      },
      {
        id: "event_description",
        name: "Event Description",
        description: "Details about the event",
        type: "textarea"
      }
    ]
  },
  {
    id: "volunteer",
    name: "Volunteer Recruitment",
    description: "Recruit volunteers for your cause",
    fields: [
      {
        id: "role_name",
        name: "Role Name",
        description: "Title of the volunteer position",
        type: "text"
      },
      {
        id: "time_commitment",
        name: "Time Commitment",
        description: "Expected hours/duration",
        type: "text"
      },
      {
        id: "role_description",
        name: "Role Description",
        description: "Details about the volunteer opportunity",
        type: "textarea"
      }
    ]
  }
]

const emailTones = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'enthusiastic', label: 'Enthusiastic' }
]

const emailLengths = [
  { value: 'short', label: 'Short' },
  { value: 'medium', label: 'Medium' },
  { value: 'long', label: 'Long' }
]

export function WriteEmailPage({ profile, session }: { profile: any | null, session: any | null }) {
  const [selectedCategory, setSelectedCategory] = useState<EmailCategory | null>(null)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [generatedEmail, setGeneratedEmail] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<'subject' | 'body' | 'signature' | null>(null)
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    tone: 'professional',
    length: 'medium',
    language: LANGUAGES[0].value,
    includeImages: false
  })
  const { toast } = useToast()

  const generateEmail = async () => {
    if (!selectedCategory) {
      toast({
        title: "Error",
        description: "Please select an email type",
        variant: "destructive"
      })
      return
    }

    setLoading(true)
    setError(null)

    try {
      if (!session?.access_token) {
        throw new Error('No access token available')
      }

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          type: selectedCategory.id,
          context: formData,
          settings: {
            tone: emailSettings.tone || 'professional',
            length: emailSettings.length || 'medium',
            format: 'plain',
            language: emailSettings.language || 'en',
            includeImages: emailSettings.includeImages
          }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to generate email')
      }

      const { data } = await response.json()
      
      // Update the form with generated content
      setGeneratedEmail({
        ...data,
        imageSuggestions: data.imageSuggestions
      })

      toast({
        title: "Success",
        description: "Email generated successfully!"
      })
    } catch (err) {
      console.error('Error generating email:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate email')
      toast({
        title: "Error",
        description: "Failed to generate email. Please try again.",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async (text: string, type: 'subject' | 'body' | 'signature') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
      toast({
        title: "Copied!",
        description: `${type.charAt(0).toUpperCase() + type.slice(1)} copied to clipboard`
      })
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive"
      })
    }
  }

  // Function to format text with line breaks
  const formatText = (text: string | null | undefined) => {
    if (!text) return null;
    return text.split('\\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        <br />
      </React.Fragment>
    ))
  }

  return (
    <div className="space-y-6">
      <LoadingDialog 
        open={loading}
        title="Generating Email"
        description="Please wait while we craft your email using AI..."
      />
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">AI Email Writer</h1>
        <p className="text-muted-foreground">
          Generate professional emails powered by AI
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 space-y-6">
          <div>
            <Label className="text-lg font-semibold">Email Type</Label>
            <RadioGroup
              value={selectedCategory?.id}
              onValueChange={(value) => {
                const category = emailCategories.find((c) => c.id === value)
                if (category) {
                  setSelectedCategory(category)
                  setFormData({})
                  setGeneratedEmail(null)
                }
              }}
              className="grid grid-cols-1 gap-4 mt-3"
            >
              {emailCategories.map((category) => (
                <div key={category.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={category.id} id={category.id} />
                  <Label htmlFor={category.id} className="flex flex-col">
                    <span className="font-medium">{category.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {category.description}
                    </span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-4">
            <Label className="text-lg font-semibold">Email Settings</Label>
            
            <div className="space-y-2">
              <Label>Language</Label>
              <Select
                value={emailSettings.language}
                onValueChange={(value) =>
                  setEmailSettings((prev) => ({ ...prev, language: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label} {lang.nativeName && `(${lang.nativeName})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tone</Label>
              <Select
                value={emailSettings.tone}
                onValueChange={(value) =>
                  setEmailSettings((prev) => ({ ...prev, tone: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tone" />
                </SelectTrigger>
                <SelectContent>
                  {emailTones.map((tone) => (
                    <SelectItem key={tone.value} value={tone.value}>
                      {tone.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Length</Label>
              <Select
                value={emailSettings.length}
                onValueChange={(value: "short" | "medium" | "long") =>
                  setEmailSettings((prev) => ({ ...prev, length: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select length" />
                </SelectTrigger>
                <SelectContent>
                  {emailLengths.map((length) => (
                    <SelectItem key={length.value} value={length.value}>
                      {length.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="images"
                checked={emailSettings.includeImages}
                onCheckedChange={(checked) =>
                  setEmailSettings((prev) => ({ ...prev, includeImages: checked }))
                }
              />
              <Label htmlFor="images">Include image suggestions</Label>
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-lg font-semibold">Email Details</Label>
            {selectedCategory?.fields.map((field) => (
              <div key={field.id} className="space-y-2">
                <Label htmlFor={field.id}>
                  {field.name}
                  <span className="text-sm text-muted-foreground ml-1">
                    {field.description}
                  </span>
                </Label>
                {field.type === "textarea" ? (
                  <Textarea
                    id={field.id}
                    value={formData[field.id] || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [field.id]: e.target.value,
                      }))
                    }
                    rows={4}
                  />
                ) : (
                  <Input
                    id={field.id}
                    value={formData[field.id] || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [field.id]: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            ))}
          </div>

          <Button
            onClick={generateEmail}
            disabled={loading}
            className="w-full"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Generate Email
          </Button>
        </Card>

        {generatedEmail && (
          <Card className="p-6 space-y-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">Subject</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedEmail.subject, 'subject')}
                  >
                    {copied === 'subject' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="p-3 bg-muted rounded-md">
                  {generatedEmail.subject}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">Body</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedEmail.body, 'body')}
                  >
                    {copied === 'body' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="p-3 bg-muted rounded-md whitespace-pre-wrap">
                  {formatText(generatedEmail.body)}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">Signature</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedEmail.signature, 'signature')}
                  >
                    {copied === 'signature' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="p-3 bg-muted rounded-md">
                  {formatText(generatedEmail.signature)}
                </div>
              </div>

              {generatedEmail.imageSuggestions && generatedEmail.imageSuggestions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-lg font-semibold">Suggested Images</Label>
                  <ul className="list-disc list-inside p-3 bg-muted rounded-md space-y-1">
                    {generatedEmail.imageSuggestions.map((image: string, index: number) => (
                      <li key={index}>{image}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
